import { useEffect } from "react";
import { api } from "@/lib/api";

export const HERO_IMAGE = "/api/uploads/hero_home.webp";

export interface SiteSettings {
  site_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  social_instagram: string | null;
  social_instagram_url: string | null;
  social_tiktok: string | null;
  vat_number: string | null;
  address: string | null;
  contact_eyebrow: string | null;
  contact_title: string | null;
  contact_description: string | null;
  contact_cta_label: string | null;
  seo_title: string | null;
  seo_description: string | null;
  privacy_policy: string | null;
  cookie_policy: string | null;
  terms_conditions: string | null;
  palette?: { black?: string; white?: string; fuchsia?: string; lime?: string };
  integrations?: { ga4_measurement_id?: string | null };
}

export interface PortfolioProject {
  id: string;
  title: string;
  slug: string | null;
  description: string | null;
  cover_image_url: string | null;
  theme_id: string | null;
  model_id: string | null;
  featured: boolean;
  sort_order: number;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
}

export interface Review {
  id: string;
  author_name: string;
  text: string;
  sort_order: number;
}

export interface ContentPage {
  id?: string;
  key: string;
  eyebrow?: string | null;
  title: string | null;
  description: string | null;
  button_text: string | null;
  button_url: string | null;
  whatsapp_message?: string | null;
  images: string[];
  published?: boolean;
  sort_order?: number;
}

export const getSettings = () => api.get<SiteSettings>("/settings").then((r) => r.data);
export const getPortfolio = () => api.get<PortfolioProject[]>("/portfolio").then((r) => r.data);
export const getFaqs = () => api.get<Faq[]>("/faqs").then((r) => r.data);
export const getReviews = () => api.get<Review[]>("/reviews").then((r) => r.data);
export const getContentPages = () => api.get<ContentPage[]>("/pages").then((r) => r.data).catch(() => [] as ContentPage[]);
export const getContentPage = (key: string) =>
  api.get<ContentPage>(`/pages/${key}`).then((r) => r.data).catch(() => null);

/**
 * Canonical homepage content. Used both as the editable seed values and as the
 * safe fallback when a section is missing/unpublished in the CMS — so the page
 * never breaks and its look never changes.
 */
export const HOME_DEFAULTS: Record<string, Record<string, any>> = {
  hero: {
    eyebrow: "Custom sneakers · dipinte a mano",
    title_line1: "La tua idea.",
    title_line2: "Realizzata a mano",
    paragraph: "Ogni paio nasce da un progetto unico, dipinto interamente a mano e creato su misura per te.",
    cta_primary: "Configura la tua",
    cta_secondary: "Vedi il portfolio",
    badge: "One-of-one",
    image_url: HERO_IMAGE,
  },
  ticker: { items: ["Anime", "Film", "Videogiochi", "Sport", "Solo colori", "Custom", "Fatto a mano"] },
  how: {
    eyebrow: "Come funziona",
    title_line1: "La tua idea",
    title_line2: "prende forma.",
    description: "Un processo semplice e trasparente. Nessun acquisto online: prima definisco tutto con te.",
    steps: [
      { title: "Configura", text: "Scegli categoria, modello, tema e livello di dettaglio nel configuratore interattivo." },
      { title: "Dipingo a mano", text: "Realizzo ogni sneaker artigianalmente, con colori professionali e finiture protettive." },
      { title: "Ricevi il tuo paio", text: "Ti ricontatto con la stima, confermo i dettagli con te e realizzo la tua idea." },
    ],
  },
  portfolio: {
    eyebrow: "Portfolio",
    title: "Lavori recenti",
    link_label: "Tutti",
    empty_title: "La galleria arriva presto",
    empty_text: "Intanto puoi già immaginare la tua e configurarla.",
    empty_cta: "Inizia ora",
  },
  reviews: { title: "Cosa dicono i clienti" },
  faq: { eyebrow: "Domande frequenti", title: "FAQ", link_label: "Tutte le FAQ →" },
  cta: { title_line1: "Pronto a creare", title_line2: "il tuo paio?", cta_label: "Apri il configuratore" },
  promo: {
    eyebrow: "Occasioni speciali",
    title: "Non solo sneakers",
    text: "Personalizzazioni su misura per ogni esigenza.",
    cards: [
      { title: "Scarpe da cerimonia", text: "Per matrimoni e occasioni speciali, dipinte a mano.", button_text: "Scopri", button_link: "/scarpe-da-cerimonia", image_url: "" },
      { title: "Aziende / Bulk", text: "Ordini multipli e regali aziendali personalizzati.", button_text: "Scopri", button_link: "/aziende", image_url: "" },
    ],
  },
};

export type HomeSections = Record<string, Record<string, any>>;

function mergeSection(def: Record<string, any>, got?: Record<string, any>): Record<string, any> {
  const out = { ...def };
  if (got) {
    for (const k of Object.keys(got)) {
      const v = got[k];
      if (v === null || v === undefined) continue;
      if (typeof v === "string" && v.trim() === "") continue;
      if (Array.isArray(v) && v.length === 0) continue;
      out[k] = v;
    }
  }
  return out;
}

/** Fetch published homepage sections merged over the safe defaults, keyed by section key. */
export const getHomepageContent = (): Promise<HomeSections> =>
  api
    .get<any[]>("/homepage-sections")
    .then((r) => {
      const raw: Record<string, Record<string, any>> = {};
      (r.data || []).forEach((s) => {
        raw[s.key] = { ...(s.data || {}), image_url: s.image_url ?? undefined };
      });
      const out: HomeSections = {};
      for (const key of Object.keys(HOME_DEFAULTS)) {
        if (key === "promo") {
          // Promo block is opt-in: only show when the CMS section exists & is published.
          if (raw[key]) out[key] = mergeSection(HOME_DEFAULTS[key], raw[key]);
        } else {
          out[key] = mergeSection(HOME_DEFAULTS[key], raw[key]);
        }
      }
      return out;
    })
    .catch(() => {
      const out: HomeSections = {};
      for (const key of Object.keys(HOME_DEFAULTS)) {
        if (key !== "promo") out[key] = { ...HOME_DEFAULTS[key] };
      }
      return out;
    });

/** Resolve the Instagram profile URL from the CMS handle or explicit URL. */
export function igUrl(s?: SiteSettings | null): string | null {
  if (!s) return null;
  const explicit = s.social_instagram_url?.trim();
  if (explicit) return /^https?:\/\//i.test(explicit) ? explicit : `https://${explicit}`;
  const handle = s.social_instagram?.trim();
  if (!handle) return null;
  if (/^https?:\/\//i.test(handle)) return handle;
  return `https://instagram.com/${handle.replace(/^@/, "")}`;
}

/** Display handle like "@bonkercustom" from the CMS value. */
export function igHandle(s?: SiteSettings | null): string {
  const fromUrl = (u?: string | null) => {
    const seg = (u || "").replace(/\/+$/, "").split("/").pop();
    return seg ? `@${seg.replace(/^@/, "")}` : "Instagram";
  };
  const handle = s?.social_instagram?.trim();
  if (handle) return /^https?:\/\//i.test(handle) ? fromUrl(handle) : `@${handle.replace(/^@/, "")}`;
  const url = s?.social_instagram_url?.trim();
  if (url) return fromUrl(url);
  return "Instagram";
}

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Lightweight per-page SEO: title + description + Open Graph. */
export function useSeo(opts: { title: string; description?: string; image?: string }) {
  const { title, description, image } = opts;
  useEffect(() => {
    document.title = title;
    if (description) setMeta("description", description);
    setMeta("og:title", title, "property");
    if (description) setMeta("og:description", description, "property");
    if (image) setMeta("og:image", image, "property");
    setMeta("og:type", "website", "property");
  }, [title, description, image]);
}
